/**
 * Demo-org signup page. Public, no auth. Model D — passwordless
 * registration; a returning demo user can log in later with the same
 * email via /api/demo/login. This page owns both flows.
 *
 * Backend contract:
 *   POST /api/demo/register  { name, email, age }
 *   POST /api/demo/login     { email }
 * Both return { access_token, user }.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoRegisterPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('register'); // 'register' | 'login'
  const [form, setForm] = useState({ name: '', email: '', companyName: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const finish = (data) => {
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    navigate('/');
    // Full reload so AuthContext picks up the new token cleanly.
    window.location.reload();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (mode === 'register') {
        const res = await axios.post(`${API}/demo/register`, {
          name: form.name.trim(),
          email: form.email.trim(),
          company_name: form.companyName.trim() || null,
        });
        finish(res.data);
      } else {
        const res = await axios.post(`${API}/demo/login`, { email: form.email.trim() });
        finish(res.data);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', border: '1.5px solid #E5E7EB',
    borderRadius: 10, fontSize: 15, fontFamily: 'inherit', outline: 'none',
    background: '#FFFFFF',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
      <div style={{ width: '100%', maxWidth: 420, background: '#FFFFFF', borderRadius: 16, padding: '28px 24px', border: '1px solid #E5E7EB' }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#1F2937', marginBottom: 6 }}>
          {mode === 'register' ? 'Join the demo' : 'Welcome back'}
        </div>
        {/* <div
          data-testid="demo-sandbox-notice"
          style={{
            fontSize: 12, color: '#4B5563', background: '#F3F4F6',
            border: '1px solid #E5E7EB', borderRadius: 8,
            padding: '8px 10px', marginBottom: 12,
          }}
        >
          You're registering for a demo/sandbox space — separate from real company data. Feel free to explore!
        </div> */}
        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>
          {mode === 'register'
            ? 'Create a demo account in seconds. No password required.'
            : 'Sign back in with the email you used to register.'}
        </div>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {mode === 'register' && (
            <input
              data-testid="demo-name-input"
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              style={inputStyle}
            />
          )}
          <input
            data-testid="demo-email-input"
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            style={inputStyle}
          />
          {mode === 'register' && (
            <input
              data-testid="demo-company-input"
              type="text"
              maxLength={120}
              placeholder="Company Name (optional)"
              value={form.companyName}
              onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              style={inputStyle}
            />
          )}
          {error && (
            <div data-testid="demo-form-error" style={{ fontSize: 13, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 12px' }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            data-testid={mode === 'register' ? 'demo-register-submit' : 'demo-login-submit'}
            disabled={submitting}
            style={{
              padding: '12px 16px', background: submitting ? '#9CA3AF' : '#F26522',
              color: '#FFFFFF', border: 'none', borderRadius: 10, fontSize: 15,
              fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer',
              marginTop: 4,
            }}
          >
            {submitting ? '...' : (mode === 'register' ? 'Create demo account' : 'Log in')}
          </button>
        </form>

        <div style={{ marginTop: 18, textAlign: 'center', fontSize: 13, color: '#6B7280' }}>
          {mode === 'register' ? (
            <>Already registered?{' '}
              <button
                type="button"
                data-testid="demo-switch-to-login"
                onClick={() => { setMode('login'); setError(''); }}
                style={{ background: 'none', border: 'none', padding: 0, color: '#F26522', fontWeight: 600, cursor: 'pointer' }}
              >
                Log in
              </button>
            </>
          ) : (
            <>New here?{' '}
              <button
                type="button"
                data-testid="demo-switch-to-register"
                onClick={() => { setMode('register'); setError(''); }}
                style={{ background: 'none', border: 'none', padding: 0, color: '#F26522', fontWeight: 600, cursor: 'pointer' }}
              >
                Create a demo account
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoRegisterPage;
